/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { invoke } from './client';

export const AMI_BASE = '/api/device-mgt/power-meter-dlms/v1.0';

export interface AmiGlobalRegister {
  grn?: string | null;
  description?: string | null;
  unit?: string | null;
  uiOrder?: number | null;
}

export interface AmiReading {
  serial?: string;
  timestamp?: number;
  operationCode?: string;
  hasPartialResult?: boolean;
  response?: Record<string, unknown>;
  [field: string]: unknown;
}

export interface AmiReadingsPage {
  meterReadings: AmiReading[];
  count: number;
}

function firstArray(value: unknown): AmiGlobalRegister[] {
  if (Array.isArray(value)) {
    return value as AmiGlobalRegister[];
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['globalRegisters', 'registers', 'data']) {
      const nested = firstArray(record[key]);
      if (nested.length > 0) {
        return nested;
      }
    }
  }
  return [];
}

export async function getRegisterCatalogue(): Promise<AmiGlobalRegister[]> {
  const body = await invoke<unknown>('/devices/global-registers', {
    base: AMI_BASE,
    query: { offset: 0, limit: 0 },
  });
  return firstArray(body);
}

export async function getMeterReadings(
  serial: string,
  from: number,
  to: number,
  offset: number,
  limit: number,
): Promise<AmiReadingsPage> {
  const body = await invoke<Partial<AmiReadingsPage> | undefined>('/admin/devices/readings', {
    base: AMI_BASE,
    query: {
      serial,
      startTimestamp: from,
      endTimestamp: to,
      includePartialReadings: true,
      sort: 'timestamp',
      offset,
      limit,
    },
  });
  const meterReadings = Array.isArray(body?.meterReadings) ? body!.meterReadings : [];
  return { meterReadings, count: typeof body?.count === 'number' ? body.count : meterReadings.length };
}

export interface AmiDevice {
  id?: number;
  name?: string | null;
  type?: string | null;
  deviceIdentifier?: string | null;
  enrolmentInfo?: { status?: string | null; tags?: string[] | null } | null;
  properties?: { name?: string; value?: string | null }[] | null;
  [field: string]: unknown;
}

export interface AmiDevicesPage {
  devices: AmiDevice[];
  count: number;
}

export async function getDevices(offset: number, limit: number): Promise<AmiDevicesPage> {
  const body = await invoke<Partial<AmiDevicesPage> | undefined>('/devices', {
    base: AMI_BASE,
    query: { offset, limit },
  });
  const devices = Array.isArray(body?.devices) ? body!.devices : [];
  return { devices, count: typeof body?.count === 'number' ? body.count : devices.length };
}

export interface AmiMeterSubType {
  subTypeId?: string | number | null;
  subTypeName?: string | null;
  typeDefinition?: string | Record<string, unknown> | null;
  [field: string]: unknown;
}

export function getMeterSubType(subTypeId: string): Promise<AmiMeterSubType> {
  return invoke<AmiMeterSubType>(`/devices/subtypes/${encodeURIComponent(subTypeId)}`, { base: AMI_BASE });
}

export async function getAllMeterSubTypes(): Promise<AmiMeterSubType[]> {
  const body = await invoke<unknown>('/devices/subtypes', { base: AMI_BASE });
  if (Array.isArray(body)) {
    return body as AmiMeterSubType[];
  }
  const nested = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const list = nested.subTypes ?? nested.data;
  return Array.isArray(list) ? (list as AmiMeterSubType[]) : [];
}
